import type { ApiResult } from '../../types';

/**
 * Result of an API call: either `data` (2xx) or `error` (non-2xx / network failure)
 */
export interface InvokeResult<T> {
    data?: T;
    error?: unknown;
    response?: Response;
}

/**
 * Build object with form fields as properties (string)
 */
export function formToObject(form: HTMLFormElement) {
    // prepare object with form fields as properties

    const formData = new FormData(form);
    var formObj: Record<string, string | object> = {};
    formData.forEach((value, key) => {
        if (value) {
            if (key == 'attachment') {
                if (value !== null) {
                    formObj['attachment'] = value;
                }
            } else {
                formObj[key] = value?.toString();
            }
        }
    });
    return formObj;
}

/**
 * Resolve a relative URL against the current page (absolute URLs are used as-is)
 */
function resolveUrl(url: string): string {
    return /^https?:\/\//i.test(url) ? url : new URL(url, window.location.href).toString();
}

/**
 * Build a FormData body, matching the multipart encoding the API expects:
 * strings and files are appended as-is, dates as ISO strings, anything else as JSON
 */
function toFormData(formObj: Record<string, unknown>): FormData {
    const formData = new FormData();
    Object.entries(formObj).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }
        const values = Array.isArray(value) ? value : [value];
        values.forEach((item) => {
            if (typeof item === 'string' || item instanceof Blob) {
                formData.append(key, item);
            } else if (item instanceof Date) {
                formData.append(key, item.toISOString());
            } else {
                formData.append(key, JSON.stringify(item));
            }
        });
    });
    return formData;
}

/**
 * Read the response body according to its Content-Type
 */
async function parseBody(response: Response): Promise<any> {
    const contentType = response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() || '';
    const isText = contentType.startsWith('text/');
    const isJson = !contentType || contentType.startsWith('application/json') || contentType.endsWith('+json');

    // no body to read
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
        return isText ? '' : {};
    }
    if (isText) {
        return await response.text();
    }
    if (!isJson && /^(application|audio|image|video)\//.test(contentType)) {
        return await response.blob();
    }
    // some endpoints answer 200 with an empty body and no Content-Length: read as text first
    const text = await response.text();
    return text ? JSON.parse(text) : {};
}

/**
 * Send the request and return either the parsed body or the error reported by the API
 */
async function invoke<T>(url: string, init: RequestInit): Promise<InvokeResult<T>> {
    let response: Response | undefined;
    try {
        response = await fetch(resolveUrl(url), init);
        if (!response.ok) {
            // the API describes failures as JSON, but fall back to the raw text
            const text = await response.text();
            let error: unknown;
            try {
                error = JSON.parse(text);
            } catch {
                error = text;
            }
            return { error: error || {}, response };
        }
        return { data: (await parseBody(response)) as T, response };
    } catch (error) {
        // network failure, or a body that could not be parsed
        return { error: error || {}, response };
    }
}

/**
 * Invoke URL (POST) with Multipart Form Encoding
 */
export async function invokeMultipartEncoding(formObj: Record<string, string | object>, url: string) {
    // no Content-Type header: the browser adds it along with the multipart boundary
    return await invoke<ApiResult>(url, { method: 'POST', body: toFormData(formObj) });
}

/**
 * Invoke URL (POST) with JSON encoding
 */
export async function invokeJsonEncoding(formObj: any, url: string) {
    return await invoke<ApiResult>(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formObj),
    });
}

/**
 * Invoke URL (GET) with JSON encoding
 */
export async function invokeGet(url: string) {
    return await invoke<any>(url, { method: 'GET' });
}

/**
 * Remove accents from string
 */
export function removeAccents(inputString: string): string {
    // Normalize the string to NFD (Normalization Form Canonical Decomposition)
    // This separates base characters from their diacritical marks (accents).
    const normalizedString = inputString.normalize('NFD');

    // Use a regular expression to remove all diacritical marks.
    // The Unicode range \u0300-\u036f covers common diacritical marks.
    const cleanedString = normalizedString.replace(/[\u0300-\u036f]/g, '');

    return cleanedString;
}

/**
 * Check if the browser is Safari
 */
export function isSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
}
